<div class="ai-settings">
  <div class="ai-sr-only" role="status" aria-live="polite" aria-atomic="true">{{ announcement }}</div>

  <div class="st-head">
    <div class="st-head-copy">
      <div class="st-eyebrow">{{ org ? 'Organization' : 'Personal' }}</div>
      <h1 class="st-h1">AI Features</h1>
      <p class="st-lead" *ngIf="org">Control AI for everyone in your organization. When AI is off, members do not see any AI
        buttons, menus or settings. Changes save as soon as you flip a switch.</p>
      <p class="st-lead" *ngIf="!org && me">Choose which AI features you want to use. Changes save as soon as you flip a switch.</p>
    </div>
  </div>

  <div class="st-panel section ai-state" *ngIf="status === 'loading'" role="status" aria-live="polite">
    <mat-icon class="ai-state-icon ai-spin">autorenew</mat-icon>
    <div class="ai-copy">
      <div class="ai-label">Loading AI settings...</div>
    </div>
  </div>

  <div class="st-panel section ai-state" *ngIf="status === 'error'" role="alert">
    <mat-icon class="ai-state-icon ai-error">error_outline</mat-icon>
    <div class="ai-copy">
      <div class="ai-label">We could not load your AI settings.</div>
      <div class="ai-desc">Check your connection and try again. If this keeps happening, please report a bug.</div>
    </div>
    <button type="button" class="st-btn st-btn--primary st-btn--sm" (click)="retry()">Retry</button>
  </div>

  <div class="st-panel section ai-state" *ngIf="notAllowed">
    <mat-icon class="ai-state-icon">lock</mat-icon>
    <div class="ai-copy">
      <div class="ai-label">You do not have permission to manage AI settings.</div>
      <div class="ai-desc">Your organization administrator controls which AI features are available to you.</div>
    </div>
  </div>

  <ng-container *ngIf="status === 'ready' && org">
    <div class="st-panel section">
      <div class="ai-row ai-master">
        <div class="ai-copy">
          <div class="ai-label">AI features
            <span class="ai-pill" [class.off]="!org.aiEnabled">{{ org.aiEnabled ? 'On' : 'Off for all users' }}</span>
          </div>
          <div class="ai-desc" [id]="descId('org:master')">Master switch. When off, all AI capabilities are removed for every user and AI requests are
            refused by the server. Only admins see this page.</div>
          <ng-container *ngTemplateOutlet="rowError; context: { $implicit: 'org:master' }"></ng-container>
        </div>
        <ng-container *ngTemplateOutlet="rowStatus; context: { $implicit: 'org:master' }"></ng-container>
        <mat-slide-toggle color="primary" [checked]="org.aiEnabled" (change)="onMasterToggle($event.checked, $event.source)"
          aria-label="AI features for the organization" [aria-describedby]="descId('org:master')"></mat-slide-toggle>
      </div>

      <div class="ai-confirm" *ngIf="confirmOff" role="alertdialog" aria-labelledby="aiConfirmTitle">
        <h2 id="aiConfirmTitle" class="st-h2">Turn off AI for all users?</h2>
        <p>This immediately removes every AI feature for every member of your organization. No notes or case data are
          deleted.</p>
        <div class="ai-actions">
          <button type="button" class="st-btn st-btn--ghost st-btn--sm" (click)="cancelTurnOff()">Cancel</button>
          <button type="button" class="st-btn st-btn--danger st-btn--sm" (click)="confirmTurnOff()">Turn off AI</button>
        </div>
      </div>
    </div>

    <div class="st-panel section" [class.ai-muted]="!org.aiEnabled">
      <div class="st-panel-head">
        <mat-icon>tune</mat-icon>
        <h2 class="st-h2">Individual AI features</h2>
      </div>
      <div class="ai-row" *ngFor="let f of features">
        <div class="ai-copy">
          <div class="ai-label">{{ f.label }}</div>
          <div class="ai-desc" [id]="descId(orgKey(f.key))">{{ f.description }}</div>
          <ng-container *ngTemplateOutlet="rowError; context: { $implicit: orgKey(f.key) }"></ng-container>
        </div>
        <ng-container *ngTemplateOutlet="rowStatus; context: { $implicit: orgKey(f.key) }"></ng-container>
        <mat-slide-toggle color="primary" [checked]="orgFeatureOn(f.key)" [disabled]="!org.aiEnabled"
          (change)="setOrgFeature(f.key, $event.checked)" [aria-label]="labelFor(orgKey(f.key))"
          [aria-describedby]="descId(orgKey(f.key))"></mat-slide-toggle>
      </div>
    </div>

    <div class="st-panel section" [class.ai-muted]="!org.aiEnabled">
      <div class="ai-row">
        <div class="ai-copy">
          <div class="ai-label">Allow users to manage their own AI setting</div>
          <div class="ai-desc" [id]="descId('org:allow')">Members may turn AI off for their own account. They can never turn on a feature the
            organization has turned off.</div>
          <ng-container *ngTemplateOutlet="rowError; context: { $implicit: 'org:allow' }"></ng-container>
        </div>
        <ng-container *ngTemplateOutlet="rowStatus; context: { $implicit: 'org:allow' }"></ng-container>
        <mat-slide-toggle color="primary" [checked]="org.allowUserAiOptOut" [disabled]="!org.aiEnabled"
          (change)="setAllowOptOut($event.checked)" aria-label="Allow users to manage their own AI setting"
          [aria-describedby]="descId('org:allow')"></mat-slide-toggle>
      </div>
    </div>

  </ng-container>

  <ng-container *ngIf="status === 'ready' && me">
    <div class="st-panel section">
      <div class="st-panel-head" *ngIf="org">
        <mat-icon>person</mat-icon>
        <h2 class="st-h2">My AI settings</h2>
      </div>
      <div class="ai-row ai-master">
        <div class="ai-copy">
          <div class="ai-label">AI features</div>
          <div class="ai-desc" [id]="descId('me:master')">Turn off to hide all AI buttons and stop sending your notes to AI services.</div>
          <ng-container *ngTemplateOutlet="rowError; context: { $implicit: 'me:master' }"></ng-container>
        </div>
        <ng-container *ngTemplateOutlet="rowStatus; context: { $implicit: 'me:master' }"></ng-container>
        <mat-slide-toggle color="primary" [checked]="me.aiEnabled" (change)="setMyAi($event.checked)"
          aria-label="My AI features" [aria-describedby]="descId('me:master')"></mat-slide-toggle>
      </div>
      <ng-container *ngFor="let f of features">
        <div class="ai-row" *ngIf="orgAllows(f.key) && (!view || view.enabledFeatures.includes(f.key) || me.disabledFeatures.includes(f.key) || !me.aiEnabled)">
          <div class="ai-copy">
            <div class="ai-label">{{ f.label }}</div>
            <div class="ai-desc" [id]="descId(myKey(f.key))">{{ f.description }}</div>
            <ng-container *ngTemplateOutlet="rowError; context: { $implicit: myKey(f.key) }"></ng-container>
          </div>
          <ng-container *ngTemplateOutlet="rowStatus; context: { $implicit: myKey(f.key) }"></ng-container>
          <mat-slide-toggle color="primary" [checked]="myFeatureOn(f.key)" [disabled]="!me.aiEnabled"
            (change)="setMyFeature(f.key, $event.checked)" [aria-label]="labelFor(myKey(f.key))"
            [aria-describedby]="descId(myKey(f.key))"></mat-slide-toggle>
        </div>
      </ng-container>
    </div>
  </ng-container>

  <!-- Per switch: a small Saving / Saved marker beside it. The live region above speaks once when saving settles. -->
  <ng-template #rowStatus let-key>
    <span class="ai-status" *ngIf="toggleState[key] === 'saving'" aria-hidden="true">
      <mat-icon class="ai-status-icon ai-spin">autorenew</mat-icon>Saving
    </span>
    <span class="ai-status ai-status--saved" *ngIf="toggleState[key] === 'saved'" aria-hidden="true">
      <mat-icon class="ai-status-icon">check</mat-icon>Saved
    </span>
  </ng-template>

  <!-- Per switch: the save failed; the switch shows the server's value. Spoken through the live region. -->
  <ng-template #rowError let-key>
    <div class="ai-row-error" *ngIf="toggleState[key] === 'error'">
      <mat-icon class="ai-status-icon">error_outline</mat-icon>
      <span>Could not confirm the change to {{ labelFor(key) }}. Showing the saved value from the server.</span>
      <button type="button" class="st-btn st-btn--ghost st-btn--sm ai-retry" (click)="retryToggle(key)"
        [attr.aria-label]="'Retry saving ' + labelFor(key)">Retry</button>
    </div>
  </ng-template>
</div>
